import { ExportService } from './exportService';

type EditorBlock = {
  id: string;
  type: string;
  position: { x: number; y: number };
  size: { width: number; height: number };
  content: any;
  style: any;
  zIndex: number;
  visible?: boolean;
};

type EditorPage = {
  id: string;
  name: string;
  blocks: EditorBlock[];
  canvasSize: { width: number; height: number };
  background?: any;
};

type EditorState = {
  pages: EditorPage[];
  currentPageId: string;
  selectedBlockId: string | null;
  zoom: number;
  showGrid: boolean;
};

export type ExportFormat = 'pdf' | 'word' | 'html';

const API_BASE = import.meta.env.VITE_API_URL ?? 'http://localhost:3001';

// ─── Helpers ────────────────────────────────────────────────────────────────

function triggerBlobDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}

// ─── Download Service ────────────────────────────────────────────────────────

export const downloadService = {
  /**
   * Downloads PDF via backend Puppeteer (headless Chromium).
   * Auto-downloads, pixel-perfect, identical to Chrome "Save as PDF".
   * Route: POST /api/scopingai/export/pdf
   */
  downloadAsPDF: async (editorState: EditorState, filename = 'template.pdf'): Promise<void> => {
    const html = await ExportService.exportAsHTML(editorState);

    const firstPage = editorState.pages[0];
    const landscape = firstPage
      ? firstPage.canvasSize.width > firstPage.canvasSize.height
      : false;

    const response = await fetch(`${API_BASE}/api/scopingai/export/pdf`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': 'scopingai',
      },
      body: JSON.stringify({ html, options: { format: 'A4', landscape } }),
    });

    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: 'Unknown error' }));
      throw new Error(err.error || `PDF export failed (${response.status})`);
    }

    const blob = await response.blob();
    triggerBlobDownload(blob, filename);
  },

  /**
   * Downloads a standalone HTML file — fully self-contained with base64 images.
   */
  downloadAsHTML: async (editorState: EditorState, filename = 'template.html'): Promise<void> => {
    const html = await ExportService.exportAsHTML(editorState);
    const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
    triggerBlobDownload(blob, filename);
  },

  /**
   * Downloads a Word (.docx) document.
   */
  downloadAsWord: async (editorState: EditorState, filename = 'template.docx'): Promise<void> => {
    const { generateDocx } = await import('./docxExportService');
    const blob = await generateDocx(editorState);
    triggerBlobDownload(blob, filename);
  },
};
